import { useEffect, useState } from 'react';
import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import {
  GroupEntityTagsContext,
  GroupEntityTagsDispatchContext,
  GroupEntityTagsFetchContext,
  GroupEntityTagsLoadingContext,
  GroupEntityTagsSavingContext,
  GroupEntityTagsUpdateContext,
} from '@trinity/chat-components/contexts/group-tags/index.js';
import type { GroupTagsContextValue } from '@trinity/chat-components/contexts/group-tags/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { GroupTagUpdateItem } from '@trinity/chat';
import { useInvalidateFilesQueries } from '@/api/hooks/useInvalidateFileQueries';

type Props = IWorkspaceFolderId & ChildrenProps;

export default function GroupFolderTagsContainer({ children, folderId }: Props) {
  const [state, dispatch] = useState<GroupTagsContextValue>(GroupFolderTagsContainer.initialState);

  const apiClient = useChatApiClient();
  const invalidateFilesTableQueries = useInvalidateFilesQueries();

  const query = useQuery({
    queryKey: ['get:group/tags/folder', folderId],
    queryFn: () => {
      return apiClient.tags.getFolderTags({ folderId });
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.isSuccess && query.data?.tags) {
      dispatch(query.data.tags);
    }
  }, [query.data?.tags, query.isSuccess]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (tags: GroupTagUpdateItem[]) => {
      return apiClient.tags.updateFolderTags({
        folderId,
        tags,
      });
    },
    onSuccess: result => {
      query.refetch();
      invalidateFilesTableQueries();
    },
  });

  return (
    <GroupEntityTagsContext.Provider value={state}>
      <GroupEntityTagsLoadingContext.Provider value={query.isLoading}>
        <GroupEntityTagsFetchContext.Provider value={query.refetch}>
          <GroupEntityTagsUpdateContext.Provider value={mutateAsync}>
            <GroupEntityTagsDispatchContext.Provider value={dispatch}>
              <GroupEntityTagsSavingContext.Provider value={isPending}>
                {children}
              </GroupEntityTagsSavingContext.Provider>
            </GroupEntityTagsDispatchContext.Provider>
          </GroupEntityTagsUpdateContext.Provider>
        </GroupEntityTagsFetchContext.Provider>
      </GroupEntityTagsLoadingContext.Provider>
    </GroupEntityTagsContext.Provider>
  );
}

const initial: GroupTagsContextValue = [];

GroupFolderTagsContainer.initialState = initial;

export { GroupFolderTagsContainer };